import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { FIELD_LENGTH, InputEmail, InputText, ValidationService } from '@sm360/widget-commons-forms';
import { AnalyticsService, MEDIA_QUERIES, VIRTUAL_PAGEVIEWS as PAGES } from '@sm360/widget-commons-general';
import { Quote } from '@sm360/widget-commons-vehicle';

import LoadingButton from 'login/components/Library/LoadingButton/LoadingButton.jsx';

import CONSTANT from 'login/constants/constant.js';
import TAGS from 'login/constants/trackingTags';

import AuthenticationService from 'login/services/AuthenticationService/AuthenticationService.js';
import TokenService from 'login/services/TokenService/TokenService.js';

import 'login/components/SignIn/SignInForm/SignInForm.scss';

class SignInForm extends React.Component {
    constructor(props) {
        super(props);

        this.handleFormValidation = this.handleFormValidation.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleUsernameChange = this.handleUsernameChange.bind(this);
        this.handlePasswordChange = this.handlePasswordChange.bind(this);
        this.onFocus = this.onFocus.bind(this);

        this.state = {
            email: '',
            password: '',
            errorMessage: '',
            submitButtonEnabled: true,
            isEmailValid: false,
            isPasswordValid: false,
        };

        this.emailRequired = true;
        this.passwordRequired = true;
    }

    onFocus(name) {
        const windowGet = WindowProxy.get();
        const windowSize = window.innerWidth;
        const isMobileSize = windowSize <= MEDIA_QUERIES.MOBILE.MAX;
        const focusedElement = windowGet.document.getElementById(name);

        if (isMobileSize && focusedElement) {
            focusedElement.scrollIntoView();
        }
    }

    getAuthenticateErrorMessage(response) {
        let errorMessageKey = CONSTANT.LOGIN_ERROR_KEY.UNKNOWN_ERROR;

        if (response && response.status === 404) {
            errorMessageKey = CONSTANT.LOGIN_ERROR_KEY.INVALID_LOGIN;
        }

        return LocaleService.t(errorMessageKey);
    }

    handleSubmit(event) {
        event.preventDefault();
    }

    handleFormValidation() {
        if (this.state.isEmailValid && this.state.isPasswordValid) {
            this.setState({
                errorMessage: '',
                submitButtonEnabled: false,
            });

            AuthenticationService.authenticate({
                username: this.state.email,
                password: this.state.password,
                quote: this.props.quote,
            })
                .then(() => {
                    TokenService.initRefreshTokenJob().then(() => {
                        this.props.onLogin();

                        AnalyticsService.gaTrigger(TAGS.LOGIN_WIDGET__LOGIN_SUCCESS());
                        AnalyticsService.virtualPageView(PAGES.SIGN_IN());
                    });
                })
                .catch((error) => {
                    const errorMessage = this.getAuthenticateErrorMessage(error.response);

                    this.setState({
                        errorMessage,
                        submitButtonEnabled: true,
                    });
                });
        }
    }

    handleUsernameChange(value) {
        this.setState({
            email: value,
            isEmailValid: ValidationService.validateEmail(value, this.emailRequired),
        });
    }

    handlePasswordChange(value) {
        this.setState({
            password: value,
            isPasswordValid: !isEmpty(value),
        });
    }

    render() {
        return (
            <form className="form-login clearfix" id="login-session-form" name="loginForm" onSubmit={this.handleSubmit}>
                <InputEmail
                    isRequired={this.emailRequired}
                    isValid={this.state.isEmailValid}
                    label={LocaleService.t('LOGIN.EMAIL')}
                    valueChange={this.handleUsernameChange}
                    classNames={['styled-placeholder']}
                    value={this.state.email}
                    onFocus={this.onFocus}
                    dataTest="login.sign-in.email"
                />

                <InputText
                    type="password"
                    name="password"
                    isRequired={this.passwordRequired}
                    isValid={this.state.isPasswordValid}
                    label={LocaleService.t('LOGIN.PASSWORD')}
                    valueChange={this.handlePasswordChange}
                    value={this.state.password}
                    onFocus={this.onFocus}
                    classNames={['styled-placeholder']}
                    dataTest="login.sign-in.password"
                    maxLength={FIELD_LENGTH.PASSWORD_MAX}
                    minLength={FIELD_LENGTH.PASSWORD_MIN}
                />

                <div className="error-message">{this.state.errorMessage}</div>

                <LoadingButton
                    text={LocaleService.t('LOGIN.LOGIN')}
                    btnCallback={this.handleFormValidation}
                    btnEnabled={this.state.submitButtonEnabled}
                    dataTest="login.sign-in.submit"
                />
            </form>
        );
    }
}

SignInForm.defaultProps = {
    quote: new Quote.QuoteModel(),
};

SignInForm.propTypes = {
    onLogin: PropTypes.func.isRequired,
    quote: PropTypes.instanceOf(Quote.QuoteModel),
};

SignInForm.displayName = 'SignInForm';

export default SignInForm;
